import { useId, useState } from 'react';
import { Button, FieldError, InputGroup, Label, TextField } from '@heroui/react';

// Πεδίο κωδικού με εναλλαγή ορατότητας.
//
// Ο λόγος δεν είναι η ευκολία. Ένα πεδίο κωδικού που δεν αποκαλύπτεται οδηγεί σε
// επαναλαμβανόμενες αποτυχίες σύνδεσης όταν το πληκτρολόγιο είναι σε λάθος
// διάταξη — και η εφαρμογή είναι δίγλωσση, οπότε η ελληνική διάταξη είναι ο
// κανόνας και όχι η εξαίρεση. Το NIST SP 800-63B το συστήνει ρητά, γιατί η
// εναλλακτική που επιλέγουν οι χρήστες είναι απλούστεροι κωδικοί.
//
// Τρεις λεπτομέρειες που δεν είναι προαιρετικές:
//
// 1. Η **προεπιλογή είναι κρυμμένος** κωδικός. Η αποκάλυψη είναι ενέργεια του
//    χρήστη, όχι κατάσταση εκκίνησης.
// 2. Το `aria-label` **αλλάζει** μαζί με την κατάσταση, και το `aria-pressed`
//    ανακοινώνει ποια είναι. Ένα σταθερό «Εμφάνιση κωδικού» λέει ψέματα στη μισή
//    διάρκεια ζωής του κουμπιού.
// 3. Το εικονίδιο δεν είναι το μόνο σήμα: η ορατότητα του ίδιου του κωδικού
//    είναι το κύριο σήμα, οπότε η πληροφορία δεν μεταδίδεται μόνο με χρώμα.

export function PasswordField({
    label = 'Κωδικός',
    name = 'password',
    value,
    onChange,
    onBlur,
    errorMessage,
    isInvalid,
    autoComplete = 'current-password',
    placeholder,
    isRequired,
    className = ''
}) {
    const [isVisible, setIsVisible] = useState(false);
    const describedBy = useId();

    return (
        <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            isInvalid={isInvalid}
            isRequired={isRequired}
            className={className}
        >
            <Label>{label}</Label>
            <InputGroup>
                <InputGroup.Input
                    type={isVisible ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    aria-describedby={describedBy}
                />
                <InputGroup.Suffix>
                    <Button
                        isIconOnly
                        variant="ghost"
                        size="sm"
                        aria-label={isVisible ? 'Απόκρυψη του κωδικού' : 'Εμφάνιση του κωδικού'}
                        aria-pressed={isVisible}
                        onPress={() => setIsVisible((shown) => !shown)}
                    >
                        <i className={isVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'} aria-hidden="true" />
                    </Button>
                </InputGroup.Suffix>
            </InputGroup>
            <span id={describedBy} className="sr-only">
                Ο κωδικός είναι {isVisible ? 'ορατός' : 'κρυμμένος'}.
            </span>
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
        </TextField>
    );
}

export default PasswordField;
